import { useState, useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import type { UseFormReturn } from 'react-hook-form'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import {
  mapStaffUserToItem,
  mapVendorToItem,
  mapTaskCategoryToItem,
} from '@/lib/api/integrations/buildium.utils'
import { workflowsApiClient } from '@/lib/api/workflows.client'
import { toast } from 'sonner'
import { ROUTE_REGISTRY } from '@/lib/routes'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  INTEGRATIONS,
  ActionSubtype,
  getActionSubtypeLabel,
  getHandoffFields,
} from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { Heading } from '@/components/ui/typography'

interface HandoffActionSummary {
  workflowId: string
  workflowName: string
  actionSubtype: ActionSubtype | null
  variantId: string | null
  parameters: Record<string, unknown>
}

const numericOptional = z
  .string()
  .optional()
  .refine(
    val => !val || /^\d+$/.test(val),
    'Only numeric characters are allowed',
  )

const formSchema = z.object({
  // Work Order defaults
  defaultAssignedToUserId: z
    .string()
    .min(1, 'Default Assignee is required')
    .regex(/^\d+$/, 'Only numeric characters are allowed'),
  defaultVendorId: z
    .string()
    .min(1, 'Default Vendor is required')
    .regex(/^\d+$/, 'Only numeric characters are allowed'),
  // Resident Request defaults
  defaultResidentAssigneeId: numericOptional,
  // Rental Owner Request defaults
  defaultRentalOwnerAssigneeId: numericOptional,
  // Contact Request defaults
  defaultContactAssigneeId: numericOptional,
})

type FormValues = z.infer<typeof formSchema>

export function BuildiumIntegrationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin, isLoading: isAuthLoading } = useAuth()
  const isEditMode = searchParams.get('mode') === 'edit'
  const [existingMetadata, setExistingMetadata] = useState<{
    defaultAssignedToUserId: string
    defaultVendorId: string
    defaultResidentAssigneeId: string
    defaultRentalOwnerAssigneeId: string
    defaultContactAssigneeId: string
  }>({
    defaultAssignedToUserId: '',
    defaultVendorId: '',
    defaultResidentAssigneeId: '',
    defaultRentalOwnerAssigneeId: '',
    defaultContactAssigneeId: '',
  })
  const [isLoading, setIsLoading] = useState(isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [handoffActions, setHandoffActions] = useState<HandoffActionSummary[]>(
    [],
  )
  const [staffUserItems, setStaffUserItems] = useState<
    SearchDropdownItem<number>[]
  >([])
  const [categoryItems, setCategoryItems] = useState<
    SearchDropdownItem<number>[]
  >([])
  const [vendorItems, setVendorItems] = useState<SearchDropdownItem<number>[]>(
    [],
  )

  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(true)
  const [dropdownsFailed, setDropdownsFailed] = useState(false)

  // Fetch staff users, task categories, and vendors for dropdowns and name resolution
  useEffect(() => {
    if (!currentCompany?.id || isAuthLoading) return

    const controller = new AbortController()

    const fetchDropdownData = async () => {
      try {
        setIsLoadingDropdowns(true)
        setDropdownsFailed(false)
        const [users, categories, vendors] = await Promise.all([
          isSuperAdmin
            ? integrationsClient.buildium.getAdminStaffUsers(currentCompany.id)
            : integrationsClient.buildium.getStaffUsers(),
          isSuperAdmin
            ? integrationsClient.buildium.getAdminTaskCategories(
                currentCompany.id,
              )
            : integrationsClient.buildium.getTaskCategories(),
          isSuperAdmin
            ? integrationsClient.buildium.getAdminVendors(currentCompany.id)
            : integrationsClient.buildium.getVendors(),
        ])
        if (controller.signal.aborted) return
        const nonNull = <T,>(item: T | null): item is T => item !== null
        setStaffUserItems(users.map(mapStaffUserToItem).filter(nonNull))
        setCategoryItems(categories.map(mapTaskCategoryToItem).filter(nonNull))
        setVendorItems(vendors.map(mapVendorToItem).filter(nonNull))
      } catch (error) {
        if (controller.signal.aborted) return
        console.error('Error fetching dropdown data:', error)
        setDropdownsFailed(true)
        toast.error(
          'Unable to fetch data from Buildium. Please try again later.',
        )
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingDropdowns(false)
        }
      }
    }
    fetchDropdownData()

    return () => controller.abort()
  }, [currentCompany?.id, isSuperAdmin, isAuthLoading])

  // This page is now edit-only. Connect flow goes directly to OAuth.
  // Redirect if not in edit mode.
  useEffect(() => {
    if (!isEditMode) {
      navigate(ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path, { replace: true })
    }
  }, [isEditMode, navigate])

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      defaultAssignedToUserId: '',
      defaultVendorId: '',
      defaultResidentAssigneeId: '',
      defaultRentalOwnerAssigneeId: '',
      defaultContactAssigneeId: '',
    },
  })

  useEffect(() => {
    // Fetch existing integration status only in edit mode
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const [integrations, workflows] = await Promise.all([
          isSuperAdmin
            ? integrationsClient.getAllAdminIntegrationsStatus(
                currentCompany.id,
              )
            : integrationsClient.getAllIntegrationsStatus(),
          isSuperAdmin
            ? workflowsApiClient.getAllAdminHandoffWorkflowsForCompany(
                currentCompany.id,
              )
            : workflowsApiClient.getHandoffWorkflows(),
        ])
        const buildium = integrations.find(
          i =>
            i.type === INTEGRATIONS.BUILDIUM.TYPE ||
            i.name === INTEGRATIONS.BUILDIUM.NAME,
        )
        if (buildium?.connected && buildium.metadata) {
          const metadata = {
            defaultAssignedToUserId: String(
              buildium.metadata.defaultAssignedToUserId ?? '',
            ),
            defaultVendorId: String(buildium.metadata.defaultVendorId ?? ''),
            defaultResidentAssigneeId: String(
              buildium.metadata.defaultResidentAssigneeId ?? '',
            ),
            defaultRentalOwnerAssigneeId: String(
              buildium.metadata.defaultRentalOwnerAssigneeId ?? '',
            ),
            defaultContactAssigneeId: String(
              buildium.metadata.defaultContactAssigneeId ?? '',
            ),
          }
          setExistingMetadata(metadata)
          form.reset(metadata)
        }

        // Extract handoff actions that reference this Buildium integration
        if (buildium?.id && workflows.length > 0) {
          const actions: HandoffActionSummary[] = []
          for (const workflow of workflows) {
            for (const group of workflow.actionGroups || []) {
              for (const action of group.actions || []) {
                if (action.integrationId === buildium.id) {
                  actions.push({
                    workflowId: workflow.id,
                    workflowName: workflow.name,
                    actionSubtype:
                      (action.actionSubtype as ActionSubtype) || null,
                    variantId:
                      (action.parameters?.taskVariant as string) || null,
                    parameters:
                      (action.parameters as Record<string, unknown>) || {},
                  })
                }
              }
            }
          }
          setHandoffActions(actions)
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing configuration')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin, form])

  const handleSubmit = async (data: FormValues) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    try {
      setIsSubmitting(true)
      const metadata = {
        defaultAssignedToUserId: data.defaultAssignedToUserId,
        defaultVendorId: data.defaultVendorId,
        defaultResidentAssigneeId: data.defaultResidentAssigneeId || undefined,
        defaultRentalOwnerAssigneeId:
          data.defaultRentalOwnerAssigneeId || undefined,
        defaultContactAssigneeId: data.defaultContactAssigneeId || undefined,
      }

      // Update metadata via existing API key credentials endpoint
      if (isSuperAdmin) {
        await integrationsClient.buildium.updateAdminMetadata(
          currentCompany.id,
          metadata,
        )
      } else {
        await integrationsClient.buildium.updateMetadata(metadata)
      }
      toast.success('Buildium configuration updated successfully!')
      navigate(ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path)
    } catch (error) {
      console.error('Error updating Buildium:', error)
      toast.error('Failed to update Buildium integration')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="h-full pt-16">
        <div className="space-y-6 p-6 pt-0 pb-24">
          <div className="flex items-center gap-3 mt-6">
            <div className="w-10 h-10 rounded-lg bg-secondary/50 animate-pulse" />
            <div className="h-6 w-48 bg-secondary/50 animate-pulse rounded" />
          </div>
          <div className="space-y-4">
            <div className="h-20 bg-secondary/50 animate-pulse rounded" />
            <div className="h-20 bg-secondary/50 animate-pulse rounded" />
          </div>
        </div>
      </div>
    )
  }

  const hasChanges =
    form.watch('defaultAssignedToUserId') !==
      existingMetadata.defaultAssignedToUserId ||
    form.watch('defaultVendorId') !== existingMetadata.defaultVendorId ||
    form.watch('defaultResidentAssigneeId') !==
      existingMetadata.defaultResidentAssigneeId ||
    form.watch('defaultRentalOwnerAssigneeId') !==
      existingMetadata.defaultRentalOwnerAssigneeId ||
    form.watch('defaultContactAssigneeId') !==
      existingMetadata.defaultContactAssigneeId

  return (
    <div className="h-full pt-16">
      <FloatingActionIslandProvider>
        <div className="space-y-6 p-6 pt-0 pb-24">
          <div className="flex items-center gap-3 mt-6">
            <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={INTEGRATIONS.BUILDIUM.LOGO_PATH}
                alt={INTEGRATIONS.BUILDIUM.NAME}
                className="w-full h-full object-contain"
              />
            </div>
            <Heading variant="subsection-title">
              Update {INTEGRATIONS.BUILDIUM.NAME}
            </Heading>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              noValidate
              className="space-y-6 max-w-2xl"
            >
              {/* Work Order Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Work Orders</h3>
                <div className="space-y-4">
                  <BuildiumDropdownField
                    form={form}
                    name="defaultAssignedToUserId"
                    label="Default Assignee"
                    description="A staff user to assign work orders to is required."
                    items={staffUserItems}
                    placeholder="Select assignee..."
                    emptyMessage="No staff users found"
                    disabled={isSubmitting || isLoadingDropdowns}
                    fallback={dropdownsFailed}
                  />
                  <BuildiumDropdownField
                    form={form}
                    name="defaultVendorId"
                    label="Default Vendor"
                    description="A vendor to assign work orders to is required."
                    items={vendorItems}
                    placeholder="Select vendor..."
                    emptyMessage="No vendors found"
                    disabled={isSubmitting || isLoadingDropdowns}
                    fallback={dropdownsFailed}
                  />
                </div>
              </div>

              {/* Resident Request Section */}
              <div className="pt-4 border-t space-y-4">
                <h3 className="text-sm font-medium">Resident Requests</h3>
                <div className="space-y-4">
                  <BuildiumDropdownField
                    form={form}
                    name="defaultResidentAssigneeId"
                    label="Default Assignee"
                    description="A staff user to assign resident requests to is optional."
                    items={staffUserItems}
                    placeholder="Select assignee (optional)..."
                    emptyMessage="No staff users found"
                    disabled={isSubmitting || isLoadingDropdowns}
                    fallback={dropdownsFailed}
                  />
                </div>
              </div>

              {/* Rental Owner Request Section */}
              <div className="pt-4 border-t space-y-4">
                <h3 className="text-sm font-medium">Rental Owner Requests</h3>
                <div className="space-y-4">
                  <BuildiumDropdownField
                    form={form}
                    name="defaultRentalOwnerAssigneeId"
                    label="Default Assignee"
                    description="A staff user to assign rental owner requests to is optional."
                    items={staffUserItems}
                    placeholder="Select assignee (optional)..."
                    emptyMessage="No staff users found"
                    disabled={isSubmitting || isLoadingDropdowns}
                    fallback={dropdownsFailed}
                  />
                </div>
              </div>

              {/* Contact Request Section */}
              <div className="pt-4 border-t space-y-4">
                <h3 className="text-sm font-medium">Contact Requests</h3>
                <div className="space-y-4">
                  <BuildiumDropdownField
                    form={form}
                    name="defaultContactAssigneeId"
                    label="Default Assignee"
                    description="A staff user to assign contact requests to is optional."
                    items={staffUserItems}
                    placeholder="Select assignee (optional)..."
                    emptyMessage="No staff users found"
                    disabled={isSubmitting || isLoadingDropdowns}
                    fallback={dropdownsFailed}
                  />
                </div>
              </div>
            </form>
          </Form>

          {/* Handoff Settings - Read-only view of per-rule overrides */}
          {handoffActions.length > 0 && (
            <HandoffSettingsSection
              handoffActions={handoffActions}
              dropdownItemsMap={{
                'buildium-staff-users': staffUserItems,
                'buildium-task-categories': categoryItems,
                'buildium-vendors': vendorItems,
              }}
            />
          )}

          <FloatingActionIslandContent
            show={hasChanges}
            componentId="buildium-form"
            priority={20}
          >
            <div className="inline-flex w-full max-w-2xl">
              <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
                <span>Update your Buildium configuration</span>
              </div>

              <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={isSubmitting}
                  size="sm"
                  variant="form"
                >
                  <span>{isSubmitting ? 'Saving...' : 'Update'}</span>
                </Button>
              </div>
            </div>
          </FloatingActionIslandContent>
        </div>
        <FloatingActionIsland />
      </FloatingActionIslandProvider>
    </div>
  )
}

/**
 * Reusable dropdown field for Buildium integration settings.
 * Wraps SearchDropdown with FormField boilerplate.
 */
function BuildiumDropdownField({
  form,
  name,
  label,
  description,
  items,
  placeholder,
  emptyMessage,
  disabled,
  fallback,
}: {
  form: UseFormReturn<FormValues>
  name: keyof FormValues
  label: string
  description: string
  items: SearchDropdownItem<number>[]
  placeholder: string
  emptyMessage: string
  disabled: boolean
  fallback: boolean
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const value = field.value ?? ''
        const selected = items.find(item => item.id === value) ?? null
        return (
          <FormItem className="max-w-md">
            <FormLabel>{label}</FormLabel>
            <FormControl>
              {fallback ? (
                <Input
                  placeholder="e.g. 12345"
                  value={value}
                  onChange={e => field.onChange(e.target.value)}
                  disabled={disabled}
                />
              ) : (
                <SearchDropdown<number>
                  items={items}
                  selectedItems={selected}
                  onSelect={item => {
                    const sel = Array.isArray(item) ? item[0] : item
                    field.onChange(sel?.data != null ? String(sel.data) : '')
                  }}
                  buttonLabel={selected?.label ?? placeholder}
                  placeholder={placeholder}
                  searchPlaceholder="Search by name or email..."
                  emptyMessage={emptyMessage}
                  buttonClassName="w-full [&_.truncate]:ml-0"
                  disabled={disabled}
                />
              )}
            </FormControl>
            <FormDescription>{description}</FormDescription>
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

/**
 * Displays a read-only summary of all handoff rules using this integration,
 * grouped by action subtype, showing per-handoff field overrides.
 */
function HandoffSettingsSection({
  handoffActions,
  dropdownItemsMap,
}: {
  handoffActions: HandoffActionSummary[]
  dropdownItemsMap: Record<string, SearchDropdownItem<number>[]>
}) {
  // Group actions by actionSubtype label (e.g. "Work Order", "Task - Resident Request")
  const grouped = useMemo(() => {
    const map = new Map<string, HandoffActionSummary[]>()
    for (const action of handoffActions) {
      const label = action.actionSubtype
        ? getActionSubtypeLabel(
            action.actionSubtype,
            INTEGRATIONS.BUILDIUM.TYPE,
            action.variantId,
          )
        : 'Unknown'
      const existing = map.get(label) || []
      existing.push(action)
      map.set(label, existing)
    }
    return map
  }, [handoffActions])

  return (
    <div className="pt-6 border-t space-y-6 max-w-2xl">
      <div>
        <h3 className="text-sm font-medium">Handoff Settings</h3>
        <p className="text-sm text-muted-foreground mt-1">
          These settings override the defaults above when a specific handoff
          rule is triggered.
        </p>
      </div>

      {Array.from(grouped.entries()).map(([subtypeLabel, actions]) => {
        // Get field definitions for this subtype to display proper labels
        const sampleAction = actions[0]
        const fields = sampleAction.actionSubtype
          ? getHandoffFields(
              INTEGRATIONS.BUILDIUM.TYPE,
              sampleAction.actionSubtype,
              sampleAction.variantId,
            )
          : []

        return (
          <div key={subtypeLabel} className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">
              {subtypeLabel}
            </h4>
            <div className="space-y-2">
              {actions.map((action, idx) => (
                <div
                  key={`${action.workflowId}-${idx}`}
                  className="rounded-md border px-4 py-3"
                >
                  <p className="text-sm font-medium mb-2">
                    {action.workflowName}
                  </p>
                  {fields.length > 0 ? (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {fields.map(field => {
                        const value = action.parameters?.[field.key]
                        // Resolve numeric IDs to human-readable names via dropdown items
                        let displayValue: string = '-'
                        if (value != null && value !== '') {
                          const items = field.dropdownSource
                            ? dropdownItemsMap[field.dropdownSource]
                            : undefined
                          const match = items?.find(
                            item => item.id === String(value),
                          )
                          displayValue = match?.label ?? String(value)
                        }
                        return (
                          <div
                            key={field.key}
                            className="flex items-center gap-2 text-sm"
                          >
                            <span className="text-muted-foreground">
                              {field.label}:
                            </span>
                            <span>{displayValue}</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No overrides configured
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
